import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, ExternalLink, History, Loader2 } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import RichTextEditor from '@/components/RichTextEditor';
import { PageHistoryDialog } from '@/components/PageHistoryDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { API } from '@/App';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const EXTRA_FIELDS = {
  page_contact: [
    { key: 'email', label: 'Email de contact' },
    { key: 'phone', label: 'Téléphone (laisser vide pour le masquer)' },
    { key: 'address', label: 'Adresse' },
    { key: 'hours', label: "Horaires d'ouverture (laisser vide pour masquer)", multiline: true },
  ],
};

export default function SitePageEditorPage() {
  const { section } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ title: '', subtitle: '', content: '' });
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    axios.get(`${API}/admin/site-pages/sections/${section}`, { headers: headers() })
      .then(r => {
        setMeta(r.data.page);
        setForm({ title: '', subtitle: '', content: '', ...(r.data.data || {}) });
      })
      .catch(() => { toast.error('Page introuvable'); navigate('/admin/site-overview'); });
  }, [section, navigate, reloadKey]);

  const save = async () => {
    if (!form.title.trim()) { toast.error('Le titre est obligatoire'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/site-content/${section}`, { data: form }, { headers: headers() });
      toast.success('Page enregistrée — la version précédente est dans l\'historique');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  if (!meta) return <AdminLayout><div className="flex justify-center py-24"><Loader2 className="animate-spin text-gray-400" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-4xl" data-testid="site-page-editor">
        <button onClick={() => navigate('/admin/site-overview')} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1.5 mb-4" data-testid="site-page-editor-back">
          <ArrowLeft size={15} /> Toutes les pages
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>{meta.name}</h1>
            <p className="text-xs text-gray-400 font-mono mt-0.5">{meta.path}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild data-testid="site-page-editor-preview"><a href={meta.path} target="_blank" rel="noopener noreferrer"><ExternalLink size={15} /><span className="ml-1.5">Voir</span></a></Button>
            <Button variant="outline" size="sm" onClick={() => setShowHistory(true)} data-testid="site-page-editor-history"><History size={15} /><span className="ml-1.5">Historique</span></Button>
            <Button size="sm" onClick={save} disabled={saving} className="bg-[#35AFE4] hover:bg-[#1f9fd6] text-white" data-testid="site-page-editor-save">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}<span className="ml-1.5">Enregistrer</span>
            </Button>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-5">
          <div>
            <Label htmlFor="sp-title">Titre de la page</Label>
            <Input id="sp-title" value={form.title} maxLength={120} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="mt-1.5" data-testid="site-page-editor-title" />
          </div>
          <div>
            <Label htmlFor="sp-subtitle">Sous-titre <span className="text-gray-400 font-normal">(facultatif)</span></Label>
            <Input id="sp-subtitle" value={form.subtitle || ''} maxLength={220} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))} className="mt-1.5" data-testid="site-page-editor-subtitle" />
          </div>
          {(EXTRA_FIELDS[section] || []).map(f => (
            <div key={f.key}>
              <Label htmlFor={`sp-${f.key}`}>{f.label}</Label>
              {f.multiline ? (
                <Textarea id={`sp-${f.key}`} rows={3} value={form[f.key] || ''} onChange={e => setForm(v => ({ ...v, [f.key]: e.target.value }))} className="mt-1.5" data-testid={`site-page-editor-${f.key}`} />
              ) : (
                <Input id={`sp-${f.key}`} value={form[f.key] || ''} maxLength={200} onChange={e => setForm(v => ({ ...v, [f.key]: e.target.value }))} className="mt-1.5" data-testid={`site-page-editor-${f.key}`} />
              )}
            </div>
          ))}
          {meta.kind === 'text' && (
            <div data-testid="site-page-editor-content">
              <Label>Contenu</Label>
              <div className="mt-1.5" key={reloadKey}>
                <RichTextEditor value={form.content} onChange={val => setForm(f => ({ ...f, content: val }))} placeholder="Contenu de la page…" height="460px" />
              </div>
            </div>
          )}
        </div>
      </div>
      <PageHistoryDialog page={{ key: meta.key, name: meta.name, kind: meta.kind }} open={showHistory} onOpenChange={setShowHistory} onRestored={() => setReloadKey(k => k + 1)} />
    </AdminLayout>
  );
}
